import {
  Component,
  input,
  output,
  computed,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  lucideSearch,
  lucideSun,
  lucideCheck,
  lucideChevronDown,
} from '@ng-icons/lucide';
import { BrnPopoverImports } from '@spartan-ng/brain/popover';
import { BrnPopoverComponent } from '@spartan-ng/brain/popover';
import { BrnCommandImports } from '@spartan-ng/brain/command';
import { HlmIconDirective } from '@spartan-ng/helm/icon';
import { HlmPopoverImports } from '@spartan-ng/helm/popover';
import { HlmCommandImports } from '@spartan-ng/helm/command';
import { SolarModule } from '@features/catalog/models/module.model';

@Component({
  selector: 'app-module-selector',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgIconComponent,
    HlmIconDirective,
    ...BrnPopoverImports,
    ...BrnCommandImports,
    ...HlmPopoverImports,
    ...HlmCommandImports,
  ],
  providers: [
    provideIcons({
      lucideSearch,
      lucideSun,
      lucideCheck,
      lucideChevronDown,
    }),
  ],
  templateUrl: './module-selector.html',
  styleUrl: './module-selector.css',
})
export class ModuleSelector {
  // ViewChild für Popover-Referenz
  @ViewChild(BrnPopoverComponent, { static: true })
  popover!: BrnPopoverComponent;

  // Inputs — data is owned and fetched by the parent smart component
  modules = input<SolarModule[]>([]);
  selectedModuleId = input<string | null>(null);
  placeholder = input<string>('Modul auswählen...');
  disabled = input<boolean>(false);
  label = input<string>('Solarmodul');
  isLoading = input<boolean>(false);
  error = input<string | null>(null);

  // Outputs
  moduleSelected = output<SolarModule>();
  selectedModuleIdChange = output<string>();

  selectedModule = computed(() => {
    const moduleId = this.selectedModuleId();
    return this.modules().find((module) => module.id === moduleId);
  });

  selectModule(module: SolarModule): void {
    this.moduleSelected.emit(module);
    this.selectedModuleIdChange.emit(module.id);
    this.popover.close();
  }

  private calculateAreaM2(module: SolarModule): number {
    if (module.areaM2) {
      return module.areaM2;
    }
    return (module.height * module.width) / 10000;
  }

  getModuleSearchValue(module: SolarModule): string {
    const area = this.calculateAreaM2(module);
    return `${module.name} ${module.power}kWp ${area.toFixed(2)}m²`;
  }

  getModuleDisplay(module: SolarModule): string {
    const area = this.calculateAreaM2(module);
    return `${module.name} (${module.power}kWp • ${area.toFixed(2)}m²)`;
  }

  getSelectedModuleDisplay(): string {
    const module = this.selectedModule();
    return module ? this.getModuleDisplay(module) : this.placeholder();
  }

  getModuleAreaDisplay(module: SolarModule): string {
    return this.calculateAreaM2(module).toFixed(2);
  }
}
